<template>
    <div class="subject-pin" :class="{'subject-pin--callout': isCallout, 'subject-pin--breathe': moving, 'subject-pin--dimmed': dimmed}">
        <!-- Body (bulb + rings + dot) is translated by the collision offset;
             at offset 0,0 this is a no-op and renders the normal circle pin.
             In callout mode the bulb is a rounded square with a dynamic pointer
             aimed back at the true GPS point. -->
        <div class="subject-pin__body" :style="bodyStyle">
            <!-- Motion + dwell badge floating above the marker: "Driving for 23min"
                 / "Moving for 2min" / "Here for 2min". Non-interactive. Sits above
                 the battery bar when one is shown, otherwise just above the bulb. -->
            <span v-if="motionLabel" class="subject-pin__motion"
                  :class="{'subject-pin__motion--offline': motionOffline}"
                  :style="{top: battery ? '-30px' : '-16px'}">{{ motionLabel }}</span>
            <!-- Detached battery bar floating just above the marker (no background) -->
            <span v-if="battery" class="subject-pin__battery" title="Battery">
                <span class="subject-pin__battery-fill" :style="batteryFillStyle"/>
            </span>
            <!-- Animated rings sit behind the bulb -->
            <span v-if="tracked" class="subject-pin__track" :style="ringStyle"/>
            <span v-if="tracked || highlighted" class="subject-pin__pulse" :style="ringStyle"/>

            <!-- Body + pointer are both white and overlap; the gray border is a
                 single outline around their COMBINED silhouette (drawn by the
                 shape wrapper's outline filter), so it is one continuous border
                 with no seam where the pointer welds to the body. -->
            <div class="subject-pin__shape">
                <div class="subject-pin__bulb" :style="bulbStyle">
                    <!-- Plain HTML (not nested Vuetify) so it serializes into the
                         Leaflet divIcon. -->
                    <img v-if="photo && !photoFailed" :src="photo" :alt="name"
                         class="subject-pin__photo" @error="photoFailed = true"/>
                    <div v-else class="subject-pin__fallback" :style="{backgroundColor: bgColor}">
                        <i v-if="icon" :class="['mdi', icon]" :style="iconStyle"/>
                        <span v-else :style="initialsStyle">{{ initials }}</span>
                    </div>
                </div>
                <!-- Circle pin: downward tail at the GPS point. Callout: a curved
                     pointer rotated to aim back at the GPS point. Both white. -->
                <span v-if="!isCallout" class="subject-pin__tail" :style="tailStyle"/>
                <svg v-else class="subject-pin__svgptr" viewBox="-40 -40 80 80" width="80" height="80">
                    <path :d="PTR_PATH" fill="#ffffff" :transform="pointerTransform"/>
                </svg>
            </div>
            <!-- Small status dot for at-a-glance state -->
            <span class="subject-pin__dot" :style="dotStyle"/>
        </div>
    </div>
</template>

<script setup>
import {computed, ref} from "vue";
import {avatarColor, initials as toInitials} from "@/composables/appDesign";
import {statusMeta, subjectIcon, subjectKindOf, subjectStatusOf, lastSeenMinutes} from "@/composables/subjectKind";
import {isPhoneDevice, hasBatteryPercent} from "@/composables/usePowerSourceIcon";
import {NON_REPORTING_HOURS} from "@/composables/fleetStatus";
import {motionVerbFor} from "@/composables/motionVerb.mjs";

// A professional "tracking pin" marker: the subject's photo (or initials /
// persona icon) embedded in a colored map-pin (teardrop) shape with a pointer
// tail. The ring/tail/dot color mirrors the live status. Mirrors the photo and
// fallback rules of SubjectAvatar, but inlined as plain HTML because vue-leaflet
// serializes this markup into the divIcon and a nested Vuetify component chain
// did not paint inside the marker.
const props = defineProps({
    device: {type: Object, required: true},
    tracked: {type: Boolean, default: false},
    highlighted: {type: Boolean, default: false},
    // Collision declutter offset (pixels) applied to the bulb body; {0,0} = the
    // normal pin. Set by the DevicesMap collision engine.
    offset: {type: Object, default: () => ({x: 0, y: 0})},
});

// Pin geometry. Keep in sync with iconSize/iconAnchor in DevicesMap.vue:
// iconSize [BULB, BULB + TAIL], iconAnchor [BULB/2, tail tip]. The GPS anchor
// sits at local (ANCHOR_X, ANCHOR_Y) = the tail tip.
const BULB = 46;
const innerSize = BULB - 5; // 2.5px border each side
const ANCHOR_X = 23;
const ANCHOR_Y = 55;
const BULB_CX = 23; // bulb centre x at offset 0
const BULB_CY = 23; // bulb centre y at offset 0

const off = computed(() => ({
    x: Number(props.offset?.x) || 0,
    y: Number(props.offset?.y) || 0,
}));
// Distance from the GPS anchor to the (offset) bulb centre, to ignore tiny
// offsets (render a normal pin) rather than a degenerate callout.
const offLen = computed(() => Math.hypot(off.value.x, off.value.y - (ANCHOR_Y - BULB_CY)));
// Crowded mode: the bulb becomes a rounded SQUARE offset from the GPS point,
// with a triangular pointer aimed back at the point (per the marker guide).
const isCallout = computed(() => (off.value.x !== 0 || off.value.y !== 0) && offLen.value >= 5);
const bodyStyle = computed(() => isCallout.value
    ? {transform: `translate(${off.value.x}px, ${off.value.y}px)`}
    : {});

// Continuous dynamic pointer: a curved-sided SVG quill rotated to aim from the
// (offset) bulb centre back at the true GPS point. The body is translated by
// `off`, so in body-local coords the anchor sits at (ANCHOR-off); the vector
// bulb-centre -> anchor is therefore (-off.x, (ANCHOR_Y-BULB_CY)-off.y). Drawing
// the quill base under the opaque square (lower z-index) makes the junction read
// as a smooth curve rather than the flat junction of a CSS border triangle.
const pointerAngle = computed(() => {
    const dx = ANCHOR_X - (BULB_CX + off.value.x);
    const dy = ANCHOR_Y - (BULB_CY + off.value.y);
    return Math.atan2(dy, dx) * 180 / Math.PI;
});
const pointerTransform = computed(() => `rotate(${pointerAngle.value} 0 0)`);
// Quill pointing along +x. Its length STRETCHES to reach the true GPS point:
// the tip sits at the distance from the (offset) bulb centre to the anchor, so a
// decluttered marker's pointer actually touches its exact lat/lng instead of
// falling short (the declutter engine offsets by 52px+, well past the old fixed
// 36px tip). Floored at 36px so a small offset still reads as a proper pointer.
// The base stays at x=0, tucked under the bulb; curved sides taper to the tip.
const pointerLen = computed(() => {
    const dx = ANCHOR_X - (BULB_CX + off.value.x);
    const dy = ANCHOR_Y - (BULB_CY + off.value.y);
    return Math.max(36, Math.hypot(dx, dy));
});
const PTR_PATH = computed(() => {
    const L = pointerLen.value;
    const mid = (L * 0.66).toFixed(1);
    return `M 0,-11 Q ${mid},-7 ${L.toFixed(1)},0 Q ${mid},7 0,11 Z`;
});

const photoFailed = ref(false);
const name = computed(() => props.device?.asset_label || props.device?.label
    || props.device?.slno || props.device?.name || "Tracker");
// A device linked to a circle member shows that member's photo; otherwise the
// device's own photo.
const photo = computed(() => props.device?.member_photo || props.device?.asset_photo || props.device?.photo || "");
const initials = computed(() => toInitials(name.value));
// People show a photo or initials; other kinds show a persona/type icon.
const icon = computed(() => (subjectKindOf(props.device) === "person" ? null : subjectIcon(props.device)));
const bgColor = computed(() => avatarColor(name.value));
const color = computed(() => statusMeta(subjectStatusOf(props.device)).color);

// Animated glow ring while moving, to signal a live/updating subject.
const moving = computed(() => subjectStatusOf(props.device) === "moving");
// A member who turned location sharing off: the pin is drawn greyed on their
// frozen last-known point, so the circle sees "Sharing paused" rather than a
// member who silently vanished. The server keeps sending that point but stops
// sending the MQTT topic, so it can never start moving again.
// A just-activated device shown at its approximate seed position, not a real
// fix. Raw Postgres booleans can arrive as 't'/1, so read loosely.
const approximate = computed(() => {
    const v = props.device?.approximate;
    return v === true || v === 1 || v === "1" || v === "t" || v === "true";
});
const dimmed = computed(() => subjectStatusOf(props.device) === "sharingOff" || approximate.value);

// Compact dwell for the marker badge: "2min" / "23min" / "1h" / "2d". Server
// timestamps are UTC without a suffix, so normalise before parsing. Empty under
// 45s (not worth showing) or when unparseable.
const compactDwell = (ts) => {
    if (!ts) return "";
    const s = String(ts);
    const iso = /[zZ]|[+-]\d\d:?\d\d$/.test(s) ? s : s.replace(" ", "T") + "Z";
    const t = new Date(iso).getTime();
    if (!isFinite(t)) return "";
    const sec = (Date.now() - t) / 1000;
    if (sec < 45) return "";
    if (sec < 3600) return `${Math.floor(sec / 60)}min`;
    if (sec < 86400) return `${Math.floor(sec / 3600)}h`;
    return `${Math.floor(sec / 86400)}d`;
};

// Kind-aware motion + dwell badge shown above the marker: "Driving for 23min"
// (vehicle) / "Moving for X" (anything else) while moving;
// "Here for X" while parked. Empty when sharing off / location off / never
// reported.
// Once a tracker has been silent past the non-reporting cutoff, label the
// marker "Offline" instead of a stale "Here for X" (which would wrongly assert
// it is still at that spot). Same 30h as the server, the status chip
// (subjectStatusOf's isReporting gate) and the Offline filter chip, so the
// pill can never say Offline while the chip beside it says Parked.
const OFFLINE_LABEL_MINUTES = NON_REPORTING_HOURS * 60;
const OFFLINE_LABEL = "Offline";
const motionLabel = computed(() => {
    const d = props.device;
    const status = subjectStatusOf(d);
    if (status === "sharingOff" || status === "locationOff") return "";
    // Silent past the cutoff => "Offline", derived directly from last-heard age
    // so it overrides a stale "Here for X". Unknown timestamp (Infinity) is not
    // treated as offline.
    const silentMin = lastSeenMinutes(d);
    if (Number.isFinite(silentMin) && silentMin >= OFFLINE_LABEL_MINUTES) return OFFLINE_LABEL;
    // No 30-min gate here: a quiet tracker is not an offline one. A parked
    // subject legitimately goes silent for hours between heartbeats, so
    // subjectStatusOf's freshness-derived "offline" must not blank the badge -
    // only the cutoff rule above gets to say Offline.
    if (status === "moving") {
        const verb = motionVerbFor(subjectKindOf(d));
        const dw = compactDwell(d?.motion_started_at);
        return dw ? `${verb} for ${dw}` : verb;
    }
    // "Here for X" asserts the subject is still at this spot, so it needs at
    // least one report to stand on. A tracker we have never heard from has
    // nothing truthful to say.
    if (!Number.isFinite(silentMin)) return "";
    const dw = compactDwell(d?.motion_ended_at);
    return dw ? `Here for ${dw}` : "Here";
});
// Past the cutoff the pill flips to the non-reporting dark red so a tracker
// that has stopped reporting is unmissable on the map.
const motionOffline = computed(() => motionLabel.value === OFFLINE_LABEL);
// Small detached battery bar above the marker. battery_bars is DeviceBatteryBarsEnum
// (0 EMPTY / 1 LOW / 2 MEDIUM / 3 FULL), and it is only meaningful for SKUs that
// actually carry a battery - the API returns 0 for battery-less devices, which
// would otherwise render a misleading "dead" bar. So gate on battery1/2_status.
const battery = computed(() => {
    const d = props.device;
    // Phones AND hardware vehicles with a configured range report an exact
    // percent (battery_percent): fill to it, coloured by the alert bands
    // (<=20% critical, <=40% low).
    if (isPhoneDevice(d) || hasBatteryPercent(d)) {
        const p = d?.battery_percent;
        if (p === null || p === undefined) return null; // no reading yet
        const pc = Math.max(0, Math.min(100, Number(p)));
        const col = pc <= 20 ? "#EF4444" : pc <= 40 ? "#F59E0B" : "#22C55E";
        return {pct: pc / 100, color: col};
    }
    const hasBattery = d?.battery1_status || d?.battery2_status;
    const bars = d?.battery_bars;
    if (!hasBattery || bars == null) return null;
    const lvl = Math.max(0, Math.min(3, Number(bars)));
    const col = lvl <= 1 ? "#EF4444" : lvl === 2 ? "#F59E0B" : "#22C55E";
    return {pct: lvl / 3, color: col};
});
const batteryFillStyle = computed(() => battery.value
    ? {width: `${Math.round(battery.value.pct * 100)}%`, backgroundColor: battery.value.color}
    : {});

// White bulb. The single continuous gray border is drawn around the whole
// shape (bulb + pointer) by the .subject-pin__shape outline filter, so the bulb
// itself carries no outline - just a rounded-square radius + a soft inner
// highlight in callout mode. Status reads from the coloured dot and glow.
const bulbStyle = computed(() => isCallout.value
    ? {borderRadius: '15px', boxShadow: 'inset 0 1px 2px rgba(255, 255, 255, 0.55)'}
    : {});
// White pointer/tail - part of the same white silhouette.
const tailStyle = computed(() => ({borderTopColor: '#ffffff'}));
const dotStyle = computed(() => ({backgroundColor: color.value}));
const ringStyle = computed(() => ({borderColor: color.value}));
const initialsStyle = computed(() => ({
    color: "#ffffff",
    fontWeight: 700,
    fontSize: `${Math.round(innerSize * 0.4)}px`,
    lineHeight: 1,
}));
const iconStyle = computed(() => ({color: "#ffffff", fontSize: `${Math.round(innerSize * 0.55)}px`}));
</script>

<style scoped>
.subject-pin {
    position: relative;
    width: 46px;
    height: 60px;
}

/* Body wrapper so the bulb/rings/dot can be translated as a unit (callout). */
.subject-pin__body {
    position: absolute;
    top: 0;
    left: 0;
    width: 46px;
    height: 60px;
    transition: transform 120ms ease;
}

/* Location-sharing-off member: whole pin greyed at its frozen last-known point. */
.subject-pin--dimmed {
    opacity: 0.5;
}

/* In callout mode the GPS-anchored icon box is transparent and offset from the
   visible bulb, so make only the bulb + pointer clickable - otherwise the empty
   box would steal clicks near the cluster. */
.subject-pin--callout {
    pointer-events: none;
}

.subject-pin--callout .subject-pin__body {
    pointer-events: auto;
}

/* Single continuous border around the whole white shape (bulb + pointer). The
   four 0-blur drop-shadows trace the COMBINED silhouette, so there is no border
   at the weld between the body and the pointer; the last shadow is the soft
   drop for float. The bulb/tail/pointer themselves carry no outline. */
.subject-pin__shape {
    position: absolute;
    top: 0;
    left: 0;
    width: 46px;
    height: 60px;
    z-index: 3;
    filter:
        drop-shadow(1px 0 0 rgba(80, 80, 80, 0.95))
        drop-shadow(-1px 0 0 rgba(80, 80, 80, 0.95))
        drop-shadow(0 1px 0 rgba(80, 80, 80, 0.95))
        drop-shadow(0 -1px 0 rgba(80, 80, 80, 0.95))
        drop-shadow(0 2px 3px rgba(0, 0, 0, 0.30));
}

/* Curved callout pointer: an SVG quill centred on the bulb (viewBox centre 0,0
   sits at the bulb centre) and rotated to aim at the GPS point. z-index below
   the bulb so its base tucks under the rounded square - the junction reads as a
   curve, not the flat junction a CSS border triangle gives. */
.subject-pin__svgptr {
    position: absolute;
    top: -17px;  /* 23 (bulb centre) - 40 (half of the 80px viewBox) */
    left: -17px;
    width: 80px;
    height: 80px;
    overflow: visible;
    z-index: 2;
    pointer-events: none;
}

.subject-pin__bulb {
    position: absolute;
    top: 0;
    left: 0;
    width: 46px;
    height: 46px;
    box-sizing: border-box;
    border: 2.5px solid #ffffff;
    border-radius: 50%;
    background: #ffffff;
    overflow: hidden;
    z-index: 3;
}

.subject-pin__photo {
    width: 100%;
    height: 100%;
    object-fit: cover;
    display: block;
}

.subject-pin__fallback {
    width: 100%;
    height: 100%;
    display: flex;
    align-items: center;
    justify-content: center;
}

.subject-pin__tail {
    position: absolute;
    left: 50%;
    top: 39px;
    transform: translateX(-50%);
    width: 0;
    height: 0;
    border-left: 9px solid transparent;
    border-right: 9px solid transparent;
    border-top: 16px solid #ffffff;
    z-index: 2;
}

.subject-pin__dot {
    position: absolute;
    top: 2px;
    right: 2px;
    width: 12px;
    height: 12px;
    border-radius: 50%;
    background: #22C55E;
    border: 2px solid #ffffff;
    box-sizing: border-box;
    z-index: 4;
}

/* Followed-device persistent ring + highlight pulse, centred on the bulb. */
.subject-pin__track,
.subject-pin__pulse {
    position: absolute;
    top: 23px;
    left: 23px;
    width: 46px;
    height: 46px;
    margin: -23px 0 0 -23px;
    border-radius: 50%;
    border: 2px solid #22C55E;
    z-index: 1;
}

.subject-pin__track {
    animation: subject-track 2s ease-out infinite;
}

.subject-pin__pulse {
    animation: subject-pulse 1.4s ease-out infinite;
}

@keyframes subject-track {
    0% {
        transform: scale(0.8);
        opacity: 0.9;
    }
    100% {
        transform: scale(1.6);
        opacity: 0;
    }
}

@keyframes subject-pulse {
    0% {
        transform: scale(0.7);
        opacity: 0.8;
    }
    100% {
        transform: scale(1.5);
        opacity: 0;
    }
}

/* Whole-marker breathe while moving: the bulb+tail silhouette scales up ~1.12x
   over ~1s and eases back over ~2s. transform-origin is the tail tip (bottom
   centre) so the GPS anchor stays put - the pin only grows/shrinks, never moves.
   Purely visual: it does not touch the body translate (declutter offset). */
.subject-pin--breathe .subject-pin__shape {
    transform-origin: 50% 100%;
    animation: subject-breathe 3s ease-in-out infinite;
}

@keyframes subject-breathe {
    0%, 100% { transform: scale(1); }
    33% { transform: scale(1.12); }
}

/* Tiny battery bar floating just above the marker, detached and with no card
   background (just the battery outline + a coloured fill + a terminal nub).
   Drop-shadow keeps it legible over both light and satellite basemaps. */
.subject-pin__battery {
    position: absolute;
    top: -12px;
    left: 50%;
    transform: translateX(-50%);
    width: 22px;
    height: 10px;
    box-sizing: border-box;
    border: 1.5px solid rgba(55, 55, 55, 0.92);
    border-radius: 3px;
    /* Inset the coloured level fill so it sits padded inside the casing, like a
       real battery icon, rather than touching the outline edge-to-edge. */
    padding: 1px;
    background: transparent;
    z-index: 5;
    pointer-events: none;
    filter: drop-shadow(0 0 1px rgba(255, 255, 255, 0.95));
}

.subject-pin__battery::after {
    content: "";
    position: absolute;
    right: -4px;
    top: 50%;
    transform: translateY(-50%);
    width: 2px;
    height: 4px;
    border-radius: 0 1px 1px 0;
    background: rgba(55, 55, 55, 0.92);
}

.subject-pin__battery-fill {
    display: block;
    height: 100%;
    border-radius: 1px;
    transition: width 200ms ease;
}

/* Motion + dwell badge above the marker. Small rounded white pill, legible over
   both light and satellite basemaps via the border + shadow. Non-interactive so
   it never steals a click near a cluster. */
.subject-pin__motion {
    position: absolute;
    left: 50%;
    transform: translateX(-50%);
    white-space: nowrap;
    font-size: 10px;
    font-weight: 600;
    line-height: 1.2;
    color: #374151;
    background: #ffffff;
    border: 1px solid #D1D5DB;
    border-radius: 999px;
    padding: 1px 6px;
    box-shadow: 0 1px 2px rgba(0, 0, 0, 0.25);
    z-index: 6;
    pointer-events: none;
}

/* Past the silence cutoff the pill reads "Offline" and fills with the shared
   non-reporting dark red (STATUS_BADGE_COLOR.offline) under white text, matching
   the device-list chip and the popup pill. Deliberately not the #EF4444 danger
   red, which is reserved for SOS/critical. Geometry is unchanged. */
.subject-pin__motion--offline {
    color: #ffffff;
    background: #8B0000;
    border-color: #8B0000;
}
</style>
